import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSocket } from "../lib/useSocket.js";
import { useStorePresence } from "../lib/useStorePresence.js";
import { isCallActive, formatCallDuration } from "../lib/useCall.js";

/**
 * Liquid simulation using a spring-damper model.
 */
function useLiquidPhysics() {
    const raf = useRef(null);
    const state = useRef({
        angle: 0,
        velocity: 0,
        time: 0,
        wave1: 0,
        wave2: 0,
        impulse: 0,
    });
    const [surface, setSurface] = useState({ angle: 0, wave1: 0, wave2: 0 });

    const nudge = useCallback((force) => {
        state.current.impulse += force;
    }, []);

    useEffect(() => {
        let prev = performance.now();

        const tick = (now) => {
            const dt = Math.min((now - prev) / 1000, 0.05);
            prev = now;
            const s = state.current;

            s.time += dt;

            if (s.impulse !== 0) {
                s.velocity += s.impulse;
                s.impulse = 0;
            }

            const idleForce = Math.sin(s.time * 1.2) * 0.3;
            const stiffness = 14;
            const damping = 3.2;
            const acceleration = -stiffness * s.angle - damping * s.velocity + idleForce;

            s.velocity += acceleration * dt;
            s.angle += s.velocity * dt;

            s.wave1 += (1.8 + s.velocity * 0.4) * dt;
            s.wave2 += (2.4 - s.velocity * 0.3) * dt;

            setSurface({
                angle: s.angle,
                wave1: s.wave1,
                wave2: s.wave2,
            });

            raf.current = requestAnimationFrame(tick);
        };

        raf.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf.current);
    }, []);

    return { surface, nudge };
}

function LiquidLayer({ surface }) {
    const tiltDeg = surface.angle * (180 / Math.PI);
    const clampedTilt = Math.max(-12, Math.min(12, tiltDeg));

    const buildWavePath = (baseY, amplitude, phase, segments) => {
        const points = [];
        for (let i = 0; i <= segments; i++) {
            const x = (i / segments) * 120 - 10;
            const y = baseY + Math.sin((i / segments) * Math.PI * 2 + phase) * amplitude
                           + Math.sin((i / segments) * Math.PI * 3.5 + phase * 1.3) * (amplitude * 0.4);
            points.push(`${x},${y}`);
        }
        return `M${points[0]} ` + points.slice(1).map(p => `L${p}`).join(' ') + ' L110,70 L-10,70 Z';
    };

    const wave1Path = buildWavePath(32, 4, surface.wave1, 24);
    const wave2Path = buildWavePath(36, 3, surface.wave2, 24);

    return (
        <svg
            className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-pointer-events-none"
            viewBox="0 0 100 70"
            preserveAspectRatio="none"
            style={{
                transform: `rotate(${clampedTilt}deg)`,
                transformOrigin: '50% 70%',
            }}
        >
            <defs>
                <linearGradient id="sl-wave-back" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(255,255,255,0.18)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0.06)" />
                </linearGradient>
                <linearGradient id="sl-wave-front" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(255,255,255,0.32)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0.1)" />
                </linearGradient>
            </defs>
            <path d={wave2Path} fill="url(#sl-wave-back)" />
            <path d={wave1Path} fill="url(#sl-wave-front)" />
        </svg>
    );
}

/* --- Icon transition variants --- */

const chatIconVariants = {
    initial: { opacity: 0, scale: 0.3, y: 8 },
    animate: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
            type: "spring",
            stiffness: 350,
            damping: 18,
            mass: 0.8,
        },
    },
    exit: {
        opacity: 0,
        scale: 0.5,
        y: 6,
        transition: { duration: 0.15, ease: "easeIn" },
    },
};

const closeIconVariants = {
    initial: { opacity: 0, scale: 0.3, rotate: -180 },
    animate: {
        opacity: 1,
        scale: 1,
        rotate: 0,
        transition: {
            type: "spring",
            stiffness: 300,
            damping: 20,
            mass: 0.6,
        },
    },
    exit: {
        opacity: 0,
        scale: 0.4,
        rotate: 90,
        transition: { duration: 0.15, ease: "easeIn" },
    },
};

const badgeVariants = {
    initial: { opacity: 0, scale: 0, y: 5 },
    animate: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
            type: "spring",
            stiffness: 500,
            damping: 15,
            mass: 0.5,
        },
    },
    exit: {
        opacity: 0,
        scale: 0,
        transition: { duration: 0.12 },
    },
};

const callIconVariants = {
    initial: { opacity: 0, scale: 0.4 },
    animate: {
        opacity: 1,
        scale: 1,
        transition: { type: "spring", stiffness: 400, damping: 18 },
    },
    exit: { opacity: 0, scale: 0.5, transition: { duration: 0.15 } },
};

const PhoneIcon = ({ className }) => (
    <svg
        className={className}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
        />
    </svg>
);

const MutedIcon = ({ className }) => (
    <svg
        className={className}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15zM17 14l4-4m0 4l-4-4"
        />
    </svg>
);

const onlineVariants = {
    initial: { scale: 0, opacity: 0 },
    animate: {
        scale: 1,
        opacity: 1,
        transition: {
            type: "spring",
            stiffness: 400,
            damping: 12,
        },
    },
    exit: {
        scale: 0,
        opacity: 0,
        transition: { duration: 0.15 },
    },
};

/**
 * ChatBubble Component
 * Circular glass button with physics-driven liquid and polished icon transitions
 */
export function ChatBubble({
    isOpen,
    onClick,
    darkMode = false,
    unreadCount = 0,
    call = null,
}) {
    const { onMessageNew } = useSocket();
    const isStoreOnline = useStorePresence();
    const { surface, nudge } = useLiquidPhysics();

    // A call keeps running when the visitor minimizes the chat — surface it on
    // the bubble so the only cue isn't the microphone indicator in the tab bar.
    const showCall = !isOpen && isCallActive(call?.status);
    const isConnected = call?.status === "connected";
    const callLabel = !showCall
        ? null
        : call.status === "outgoing"
          ? "Calling…"
          : call.status === "connecting"
            ? "Connecting…"
            : formatCallDuration(call.durationSec);

    // Nudge liquid on new messages (even while mounted)
    useEffect(() => {
        const handleMsgNudge = (msg) => {
            if (!isOpen) nudge((Math.random() > 0.5 ? 1 : -1) * 5);
        };
        const cleanup = onMessageNew(handleMsgNudge);
        return () => cleanup();
    }, [isOpen, onMessageNew, nudge]);

    const handleClick = () => {
        nudge((Math.random() > 0.5 ? 1 : -1) * 8);
        if (onClick) onClick();
    };

    return (
        <motion.div
            className="tw-relative tw-w-14 tw-h-14"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.93 }}
            transition={{
                type: "spring",
                stiffness: 400,
                damping: 17,
            }}
        >
            {/* Live-call ring — expands out from behind the bubble. Rendered
                before the button so the button paints over its inner edge. */}
            <AnimatePresence>
                {showCall && (
                    <motion.span
                        key="call-ring"
                        aria-hidden="true"
                        className={`tw-absolute tw-inset-0 tw-rounded-full tw-border-2 tw-border-solid tw-pointer-events-none tw-z-0 ${
                            isConnected
                                ? "tw-border-emerald-400"
                                : "tw-border-amber-400"
                        }`}
                        initial={{ scale: 1, opacity: 0 }}
                        animate={{ scale: [1, 1.45], opacity: [0.7, 0] }}
                        exit={{ opacity: 0 }}
                        transition={{
                            duration: 1.6,
                            repeat: Infinity,
                            ease: "easeOut",
                        }}
                    />
                )}
            </AnimatePresence>

            {/* Call status pill — sits above the bubble, clear of the unread
                badge. Hidden from AT: the button's aria-label carries the state. */}
            <AnimatePresence>
                {showCall && (
                    <motion.button
                        key="call-pill"
                        type="button"
                        tabIndex={-1}
                        aria-hidden="true"
                        onClick={handleClick}
                        initial={{ opacity: 0, y: 6, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 6, scale: 0.9 }}
                        transition={{
                            type: "spring",
                            stiffness: 400,
                            damping: 25,
                        }}
                        className={`tw-absolute tw-bottom-full tw-right-0 tw-mb-3 tw-z-20 tw-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-rounded-full tw-border tw-border-solid tw-cursor-pointer tw-whitespace-nowrap tw-shadow-lg tw-shadow-black/20 ${
                            darkMode
                                ? "tw-bg-gray-900/90 tw-border-white/20"
                                : "tw-bg-primary/90 tw-border-white/30"
                        }`}
                    >
                        <span
                            className={`tw-w-1.5 tw-h-1.5 tw-rounded-full tw-animate-pulse ${
                                isConnected
                                    ? "tw-bg-emerald-400"
                                    : "tw-bg-amber-300"
                            }`}
                        />
                        <span className="tw-text-[11px] tw-font-semibold tw-text-white tw-tabular-nums tw-leading-none">
                            {callLabel}
                        </span>
                        {call?.muted && (
                            <MutedIcon className="tw-w-3 tw-h-3 tw-text-amber-300" />
                        )}
                    </motion.button>
                )}
            </AnimatePresence>

            {/* Unread badge — outside overflow clip */}
            <AnimatePresence>
                {!isOpen && unreadCount > 0 && (
                    <motion.span
                        key="badge"
                        variants={badgeVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        className="tw-absolute tw--top-1.5 tw--right-1.5 tw-min-w-[20px] tw-h-[20px] tw-px-1 tw-rounded-full tw-text-[10px] tw-leading-[20px] tw-font-bold tw-text-white tw-bg-red-500 tw-text-center tw-z-20 tw-shadow-sm"
                    >
                        {unreadCount > 99 ? "99+" : unreadCount}
                    </motion.span>
                )}
            </AnimatePresence>

            {/* Online indicator — outside overflow clip */}
            <AnimatePresence>
                {isStoreOnline && (
                    <motion.span
                        key="online-indicator"
                        variants={onlineVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        className={`tw-absolute tw--bottom-0.5 tw--right-0.5 tw-w-4 tw-h-4 tw-bg-green-500 tw-rounded-full tw-z-20 tw-border-2 tw-shadow-sm ${
                            darkMode ? "tw-border-gray-900" : "tw-border-white"
                        }`}
                    />
                )}
            </AnimatePresence>

            {/* Main button */}
            <button
                onClick={handleClick}
                onMouseEnter={() => nudge(3)}
                onMouseLeave={() => nudge(-2)}
                className={`
                    tw-w-14 tw-h-14 tw-rounded-full tw-shadow-lg
                    tw-flex tw-items-center tw-justify-center
                    tw-cursor-pointer tw-border
                    tw-overflow-hidden
                    hover:tw-shadow-xl
                    tw-relative
                    ${darkMode
                        ? "tw-bg-white/10 tw-border-white/20 tw-shadow-black/40"
                        : "tw-bg-primary/90 tw-border-white/30"
                    }
                `}
                style={{
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                }}
                aria-label={
                    isOpen
                        ? "Close support chat"
                        : showCall
                          ? `Open support chat — voice call ${callLabel}`
                          : "Open support chat"
                }
            >
                {/* Liquid animation layer */}
                <LiquidLayer surface={surface} />

                {/* Glass highlight */}
                <div
                    className="tw-absolute tw-inset-0 tw-rounded-full tw-pointer-events-none"
                    style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 45%, rgba(255,255,255,0.05) 100%)',
                    }}
                />

                {/* Icon swap */}
                <AnimatePresence mode="wait" initial={false}>
                    {isOpen ? (
                        <motion.svg
                            key="close"
                            className="tw-w-6 tw-h-6 tw-text-white tw-relative tw-z-[5]"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            variants={closeIconVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                        >
                            <motion.path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2.5}
                                d="M6 18L18 6M6 6l12 12"
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 1 }}
                                transition={{ duration: 0.35, delay: 0.1, ease: "easeOut" }}
                            />
                        </motion.svg>
                    ) : showCall ? (
                        <motion.div
                            key="call"
                            className="tw-relative tw-z-[5] tw-text-white"
                            variants={callIconVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                        >
                            <motion.div
                                animate={{ scale: [1, 1.12, 1] }}
                                transition={{
                                    duration: 1.6,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                }}
                            >
                                <PhoneIcon className="tw-w-6 tw-h-6" />
                            </motion.div>
                        </motion.div>
                    ) : (
                        <motion.svg
                            key="chat"
                            className="tw-w-6 tw-h-6 tw-text-white tw-relative tw-z-[5]"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            variants={chatIconVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                        >
                            <motion.path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 1 }}
                                transition={{ duration: 0.4, ease: "easeOut" }}
                            />
                            {/* Chat dots appear after bubble draws */}
                            <motion.circle
                                cx="8" cy="12" r="0.8" fill="currentColor" stroke="none"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.35, type: "spring", stiffness: 500, damping: 15 }}
                            />
                            <motion.circle
                                cx="12" cy="12" r="0.8" fill="currentColor" stroke="none"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.42, type: "spring", stiffness: 500, damping: 15 }}
                            />
                            <motion.circle
                                cx="16" cy="12" r="0.8" fill="currentColor" stroke="none"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.49, type: "spring", stiffness: 500, damping: 15 }}
                            />
                        </motion.svg>
                    )}
                </AnimatePresence>
            </button>
        </motion.div>
    );
}

export default ChatBubble;
